import React from 'react';
import { formatCurrencyInput } from '../utils/currencyUtils';
import { FILTER_SHEET_ANIM_MS } from '../hooks/useMatchFilters';
import { OFFER_FILTER_OPTIONS } from '../utils/offerMode';

/**
 * Bottom-sheet (mobile) + slide-in side panel (desktop) for filter
 * configuration. Verbatim port of the sheet JSX that lived inline in
 * Matches.jsx prior to this extraction.
 *
 * Renders nothing when `filters.isFilterSheetOpen` is false. Caller mounts
 * the component every render — visibility is internal so the close-animation
 * can run even after `isFilterSheetClosing` flips back to false on mount of
 * a new instance.
 *
 * Self-contained styling: ships its own keyframes + responsive
 * `display: none` rules. Nothing for the parent page to coordinate.
 *
 * @param {object} props
 * @param {object} props.filters - Return object from `useMatchFilters`.
 */
export default function MatchFilterSheet({ filters }) {
  const {
    filterBrands, setFilterBrands,
    filterModels, setFilterModels,
    filterEffectTypes, setFilterEffectTypes,
    filterPrice, setFilterPrice,
    filterConditionMin, setFilterConditionMin,
    filterDecades, setFilterDecades,
    filterOffers, setFilterOffers,
    categoryFilter, setCategoryFilter,

    availableBrands,
    availableModels,
    availableEffectTypes,
    availableDecades,

    isFilterSheetOpen,
    isFilterSheetClosing,
    filterSubPage,
    setFilterSubPage,
    closeFilterSheet,

    activeFilterCount,
    clearAllFilters
  } = filters;

  if (!isFilterSheetOpen) return null;

  const CONDITIONS = ['New', 'Excellent', 'Very Good', 'Good', 'Poor', 'Damaged'];
  const CATEGORY_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'pedals', label: 'Pedals' },
    { value: 'accessories', label: 'Accessories' },
  ];
  const categoryLabel = CATEGORY_OPTIONS.find(o => o.value === categoryFilter)?.label ?? 'All';

  const chipStyle = (selected) => ({
    padding: '8px 18px',
    borderRadius: '9999px',
    border: selected ? '1.5px solid #C9A227' : '1.5px solid #d1d5db',
    backgroundColor: selected ? 'rgba(201, 162, 39, 0.12)' : 'white',
    color: selected ? '#946F00' : '#374151',
    fontWeight: selected ? '700' : '500',
    fontSize: '0.9rem',
    fontFamily: 'Nunito, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  });

  const rowStyle = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1rem 1.25rem',
    cursor: 'pointer',
    borderBottom: '1px solid #f3f4f6'
  };

  const badgeArr = (arr) => arr.length > 0
    ? <span style={{ fontSize: '0.85rem', color: '#C89F3C', fontWeight: '700' }}>({arr.length})</span>
    : null;
  const badgeStr = (val) => val
    ? <span style={{ fontSize: '0.85rem', color: '#C89F3C', fontWeight: '700' }}>(1)</span>
    : null;

  const chevronRight = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
  const chevronLeft = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );

  const hasAnyFilter = activeFilterCount > 0;

  const renderMainPage = () => (
    <div style={{ fontFamily: 'Nunito, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6' }}>
        <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#374151' }}>Filters</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={clearAllFilters}
            disabled={!hasAnyFilter}
            style={{
              background: 'none',
              border: 'none',
              color: hasAnyFilter ? '#C89F3C' : '#9CA3AF',
              fontFamily: 'Nunito, sans-serif',
              fontSize: '0.95rem',
              fontWeight: '700',
              cursor: hasAnyFilter ? 'pointer' : 'default',
              padding: 0,
              opacity: hasAnyFilter ? 1 : 0.6
            }}
          >
            Clear all
          </button>
          <button onClick={closeFilterSheet} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>
      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6' }}>
        <p style={{ margin: '0 0 0.75rem 0', fontWeight: '700', fontSize: '0.95rem', color: '#5A9ECB' }}>Price Range</p>
        <div style={{ display: 'flex', gap: '0.75rem', width: '100%', boxSizing: 'border-box', padding: '0 1rem' }}>
          <input
            type="number"
            placeholder="Min $"
            value={filterPrice.min}
            onChange={e => setFilterPrice(p => ({ ...p, min: formatCurrencyInput(e.target.value) }))}
            style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '0.4rem 1rem', borderRadius: '25px', border: '1px solid #C89F3C', fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', outline: 'none' }}
          />
          <input
            type="number"
            placeholder="Max $"
            value={filterPrice.max}
            onChange={e => setFilterPrice(p => ({ ...p, max: formatCurrencyInput(e.target.value) }))}
            style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '0.4rem 1rem', borderRadius: '25px', border: '1px solid #C89F3C', fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', outline: 'none' }}
          />
        </div>
      </div>
      {availableBrands.length > 0 && (
        <div style={rowStyle} onClick={() => setFilterSubPage('brands')}>
          <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Brand {badgeArr(filterBrands)}</span>
          {chevronRight}
        </div>
      )}
      {availableModels.length > 0 && (
        <div style={rowStyle} onClick={() => setFilterSubPage('models')}>
          <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Model {badgeArr(filterModels)}</span>
          {chevronRight}
        </div>
      )}
      {availableEffectTypes.length > 0 && (
        <div style={rowStyle} onClick={() => setFilterSubPage('effectTypes')}>
          <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Effect Type {badgeArr(filterEffectTypes)}</span>
          {chevronRight}
        </div>
      )}
      <div style={rowStyle} onClick={() => setFilterSubPage('condition')}>
        <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Min Condition {badgeStr(filterConditionMin)}</span>
        {chevronRight}
      </div>
      {availableDecades.length > 0 && (
        <div style={rowStyle} onClick={() => setFilterSubPage('decades')}>
          <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Decade {badgeArr(filterDecades)}</span>
          {chevronRight}
        </div>
      )}
      <div style={rowStyle} onClick={() => setFilterSubPage('offers')}>
        <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Offers {badgeArr(filterOffers || [])}</span>
        {chevronRight}
      </div>
      <div className="matches-filter-sheet-mobile-extras">
        <style>{`@media (min-width: 769px) { .matches-filter-sheet-mobile-extras { display: none !important; } }`}</style>
        <div style={{ ...rowStyle, borderBottom: 'none' }} onClick={() => setFilterSubPage('category')}>
          <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Category</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem', color: '#6B7280', fontWeight: '500' }}>{categoryLabel}</span>
            {chevronRight}
          </div>
        </div>
      </div>
      <div style={{ padding: '1rem 1.25rem', paddingBottom: 'calc(1rem + 100px + env(safe-area-inset-bottom))' }}>
        <button
          onClick={closeFilterSheet}
          style={{ width: '100%', padding: '0.75rem', borderRadius: '25px', border: 'none', backgroundColor: '#C89F3C', color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' }}
        >
          Apply Filters
        </button>
      </div>
    </div>
  );

  const renderSubPage = () => {
    // Condition is single-select (min rank). Everything else is
    // multi-select chips populated from match data.
    if (filterSubPage === 'category') {
      return (
        <div style={{ fontFamily: 'Nunito, sans-serif' }}>
          <button onClick={() => setFilterSubPage(null)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            {chevronLeft}
            <span style={{ fontSize: '1.1rem', fontWeight: '400', color: '#5A9ECB' }}>Category</span>
          </button>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', padding: '1rem 1.25rem', justifyContent: 'center' }}>
            {CATEGORY_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { setCategoryFilter(opt.value); setFilterSubPage(null); }}
                style={chipStyle(categoryFilter === opt.value)}
              >
                {opt.label}
              </button>
            ))}
            <div style={{ width: '100%', height: 'calc(90px + env(safe-area-inset-bottom))', flexShrink: 0 }} />
          </div>
        </div>
      );
    }

    if (filterSubPage === 'condition') {
      return (
        <div style={{ fontFamily: 'Nunito, sans-serif' }}>
          <button onClick={() => setFilterSubPage(null)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem 1.25rem', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', textAlign: 'left' }}>
            {chevronLeft}
            <span style={{ fontSize: '1.1rem', fontWeight: '400', color: '#5A9ECB' }}>Min Condition</span>
          </button>
          <p style={{ margin: '1rem 1.25rem 0 1.25rem', fontSize: '0.85rem', color: '#6B7280' }}>
            Show pedals at this condition or better.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', padding: '1rem 1.25rem', justifyContent: 'center' }}>
            {CONDITIONS.map(opt => {
              const on = filterConditionMin === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setFilterConditionMin(on ? '' : opt)}
                  style={chipStyle(on)}
                >
                  {opt}
                </button>
              );
            })}
            <div style={{ width: '100%', height: 'calc(90px + env(safe-area-inset-bottom))', flexShrink: 0 }} />
          </div>
        </div>
      );
    }

    const cfg = {
      effectTypes: { title: 'Effect Type', options: availableEffectTypes, selected: filterEffectTypes, setter: setFilterEffectTypes },
      brands:      { title: 'Brand',       options: availableBrands,      selected: filterBrands,      setter: setFilterBrands },
      models:      { title: 'Model',       options: availableModels,      selected: filterModels,      setter: setFilterModels },
      decades:     { title: 'Decade',      options: availableDecades,     selected: filterDecades,     setter: setFilterDecades },
      offers:      { title: 'Offers',      options: OFFER_FILTER_OPTIONS, selected: filterOffers || [], setter: (updater) => setFilterOffers((prev) => { const next = typeof updater === 'function' ? updater(prev) : updater; return next.length > 1 ? [next[next.length - 1]] : next; }) }
    }[filterSubPage];

    if (!cfg) return null;

    return (
      <div style={{ fontFamily: 'Nunito, sans-serif' }}>
        <button onClick={() => setFilterSubPage(null)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem 1.25rem', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', textAlign: 'left' }}>
          {chevronLeft}
          <span style={{ fontSize: '1.1rem', fontWeight: '400', color: '#5A9ECB' }}>{cfg.title}</span>
        </button>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', padding: '1rem 1.25rem', justifyContent: 'center' }}>
          {cfg.options.length === 0 ? (
            <p style={{ color: '#6B7280', fontSize: '0.95rem', margin: '1rem 0' }}>
              No {cfg.title.toLowerCase()} options available in your current matches.
            </p>
          ) : cfg.options.map(opt => {
            const on = cfg.selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => cfg.setter(prev => on ? prev.filter(x => x !== opt) : [...prev, opt])}
                style={chipStyle(on)}
              >
                {opt}
              </button>
            );
          })}
          <div style={{ width: '100%', height: 'calc(90px + env(safe-area-inset-bottom))', flexShrink: 0 }} />
        </div>
      </div>
    );
  };

  const sheetContent = filterSubPage ? renderSubPage() : renderMainPage();

  return (
    <>
      <style>{`
        @keyframes matchesSheetFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes matchesSheetFadeOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes matchesSheetSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes matchesSheetSlideDown { from { transform: translateY(0); } to { transform: translateY(100%); } }
        @keyframes matchesPanelSlideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes matchesPanelSlideOut { from { transform: translateX(0); } to { transform: translateX(-100%); } }
        @media (max-width: 768px) { .matches-filter-panel-desktop { display: none !important; } }
        @media (min-width: 769px) { .matches-filter-sheet-mobile { display: none !important; } }
      `}</style>
      <div
        onClick={closeFilterSheet}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 999,
          animation: isFilterSheetClosing
            ? `matchesSheetFadeOut ${FILTER_SHEET_ANIM_MS}ms ease-in forwards`
            : `matchesSheetFadeIn 0.18s ease-out`
        }}
      />
      <div
        className="matches-filter-sheet-mobile"
        role="dialog" aria-modal="true" aria-label="Filter options"
        style={{
          position: 'fixed', left: 0, right: 0, bottom: 0,
          backgroundColor: 'white',
          borderTopLeftRadius: '20px', borderTopRightRadius: '20px',
          zIndex: 1001, maxHeight: '85vh', overflowY: 'auto',
          animation: isFilterSheetClosing
            ? `matchesSheetSlideDown ${FILTER_SHEET_ANIM_MS}ms ease-in forwards`
            : `matchesSheetSlideUp ${FILTER_SHEET_ANIM_MS}ms ease-out`
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px', paddingBottom: '6px' }}>
          <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: '#D1D5DB' }} />
        </div>
        {sheetContent}
      </div>
      <div
        className="matches-filter-panel-desktop"
        role="dialog" aria-modal="true" aria-label="Filter options"
        style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          width: '320px', backgroundColor: 'white',
          zIndex: 1001, overflowY: 'auto',
          animation: isFilterSheetClosing
            ? `matchesPanelSlideOut ${FILTER_SHEET_ANIM_MS}ms ease-in forwards`
            : `matchesPanelSlideIn ${FILTER_SHEET_ANIM_MS}ms ease-out`
        }}
      >
        {sheetContent}
      </div>
    </>
  );
}
